import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchDepartmentsList, 
  deleteDepartment,
  clearDepartmentError,
  fetchSpecialities
} from '../../actions/DepartmentManagementAction';
import { Button, Table, Input, Modal, message, Popconfirm, Space, Card, Row, Col, Pagination } from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined,
  EyeOutlined 
} from '@ant-design/icons';
import AddEditDepartmentModal from './AddEditDepartmentModal';
import ViewDepartmentModal from './ViewDepartmentModal';
import './DepartmentManagement.css';

const { Search } = Input;

const DepartmentManagementList = () => {
  const dispatch = useDispatch();
  const { departments, pagination, loading, error, specialities } = useSelector(state => state.departmentManagement);
  
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [editingDepartment, setEditingDepartment] = useState(null);

  useEffect(() => {
    loadDepartments();
    // Fetch specialities on component mount
    if (specialities.length === 0) {
      dispatch(fetchSpecialities());
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearDepartmentError());
    }
  }, [error, dispatch]);

  const loadDepartments = () => {
    dispatch(fetchDepartmentsList(currentPage, pageSize, searchText ));
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
    dispatch(fetchDepartmentsList(1, pageSize, value));
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleAdd = () => {
    setEditingDepartment(null);
    setIsAddModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingDepartment(record);
    setIsEditModalVisible(true);
  };

  const handleView = (record) => {
    setSelectedDepartment(record);
    setIsViewModalVisible(true);
  };

  const handleDelete = async (ssmId) => {
    try {
      await dispatch(deleteDepartment(ssmId));
      message.success('Department deleted successfully');
      loadDepartments();
    } catch (error) {
      message.error('Failed to delete department');
    }
  };

  const handleModalClose = () => {
    setIsAddModalVisible(false);
    setIsEditModalVisible(false);
    setIsViewModalVisible(false);
    setEditingDepartment(null);
    setSelectedDepartment(null);
  };

  const handleSuccess = () => {
    handleModalClose();
    loadDepartments();
    message.success(editingDepartment ? 'Department updated successfully' : 'Department added successfully');
  };

  const getSpecialityName = (specialityId) => {
    const speciality = specialities.find(s => s.SpecialityId === specialityId);
    return speciality ? speciality.specialityName : specialityId;
  };

  const columns = [
    {
      title: 'Department Name',
      dataIndex: 'ssm_secondary_speciality_name',
      key: 'ssm_secondary_speciality_name',
      render: (text) => <strong>{text}</strong>
    },
    {
      title: 'Speciality',
      dataIndex: 'specialityName',
      key: 'specialityName',
      width: 200,
      //render: (specialityId) => getSpecialityName(specialityId)
    },
    {
      title: 'Details',
      dataIndex: 'ssm_details',
      key: 'ssm_details',
      ellipsis: true,
      render: (text) => {
        if (!text) return '-';
        
        // Remove HTML tags and decode HTML entities
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        const plainText = tempDiv.textContent || tempDiv.innerText || '';
        
        return plainText.trim() || '-';
      }
    },
    {
      title: 'Status',
      dataIndex: 'ssm_status',
      key: 'ssm_status',
      width: 100,
      render: (status) => (
        <span style={{ 
          color: status === 'Active' ? '#52c41a' : '#ff4d4f',
          fontWeight: '500'
        }}>
          {status || 'Active'}
        </span>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="View Details"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit Department"
          />
          <Popconfirm
            title="Delete Department"
            description="Are you sure you want to delete this department? This action cannot be undone."
            onConfirm={() => handleDelete(record.ssm_id)}
            okText="Yes, Delete"
            cancelText="Cancel"
            placement="topRight"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete Department"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="department-management">
      <Card className="department-card">
        <Row gutter={[16, 16]} className="department-header">
          <Col xs={24} sm={12} md={8}>
            <Search
              placeholder="Search departments..."
              allowClear
              size="large"
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Col>
          <Col xs={24} sm={12} md={16} style={{ textAlign: 'right' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              size="large"
              onClick={handleAdd}
            >
              Add New Department
            </Button>
          </Col>
        </Row>

        <div className="department-table-container">
          <Table
            columns={columns}
            dataSource={departments}
            rowKey="ssm_id"
            loading={loading}
            pagination={false}
            scroll={{ x: 800 }}
            className="department-table"
          />
        </div>

        {pagination.totalPages > 1 && (
          <div className="department-pagination">
            <Pagination
              current={currentPage}
              total={pagination.totalRecords}
              showTotal={(total, range) => 
                `${range[0]}-${range[1]} of ${total} departments`
              }
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
              pageSizeOptions={['10', '20', '50', '100']}
            />
          </div>
        )}
      </Card>

      {/* Add Department Modal */}
      <AddEditDepartmentModal
        visible={isAddModalVisible}
        onCancel={handleModalClose}
        onSuccess={handleSuccess}
        department={null}
        mode="add"
      />

      {/* Edit Department Modal */}
      <AddEditDepartmentModal
        visible={isEditModalVisible}
        onCancel={handleModalClose}
        onSuccess={handleSuccess}
        department={editingDepartment}
        mode="edit"
      />

      {/* View Department Modal */}
      <ViewDepartmentModal
        visible={isViewModalVisible}
        onCancel={handleModalClose}
        department={selectedDepartment}
      />
    </div>
  );
};

export default DepartmentManagementList;
